import type { SortDirection } from '@tanstack/react-table';
import { ArrowNarrowDownIcon, ArrowNarrowUpIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import type { ReactNode } from 'react';
import { Column as AriaColumn, type ColumnProps as AriaColumnProps, composeRenderProps } from 'react-aria-components';

interface SortableColumnProps extends AriaColumnProps {
  /**
   * Additional CSS classes to apply to the column
   *
   * @optional
   * @type string
   */
  className?: string;
  /**
   * Current sort direction for this column.
   * - 'asc': ascending order
   * - 'desc': descending order
   * - false: not currently sorted
   *
   * @optional
   * @type {SortDirection | false}
   */
  sortDirection?: SortDirection | false;
  /**
   * Callback when the column header is clicked for sorting
   *
   * @optional
   * @type {() => void}
   */
  onSort?: () => void;
}

const columnClasses = cva({
  base: [
    'px-12 py-6 text-left font-semibold text-sm text-quaternary',
    'cursor-default whitespace-nowrap',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset',
    'focus-visible:relative focus-visible:z-20',
  ],
});

function SortIcon({ direction }: { direction?: SortDirection | false }) {
  if (direction === 'asc') {
    return <ArrowNarrowUpIcon size={16} className="ml-2" />;
  }
  if (direction === 'desc') {
    return <ArrowNarrowDownIcon size={16} className="ml-2" />;
  }
  return null;
}

/**
 * A sortable column header component that extends TableHeader.Column with sort indicators
 * and click/keyboard handling.
 *
 * @param props - The props for the SortableColumn component
 * @example
 * <TableHeader.SortableColumn
 *   sortDirection="asc"
 *   onSort={() => handleSort('email')}
 * >
 *   Email address
 * </TableHeader.SortableColumn>
 */
function SortableColumn(props: SortableColumnProps) {
  const { className: classNameProp, children, sortDirection, onSort, ...other } = props;

  return (
    <AriaColumn
      className={composeRenderProps(classNameProp, (className) => cx(columnClasses({ className })))}
      allowsSorting
      {...other}
    >
      <button
        type="button"
        className="flex items-center cursor-pointer bg-transparent border-none p-0 font-semibold text-sm text-quaternary"
        onClick={onSort}
        aria-label={`Sort by ${typeof children === 'string' ? children : ''}`}
      >
        {children as ReactNode}
        <SortIcon direction={sortDirection} />
      </button>
    </AriaColumn>
  );
}

export { SortableColumn };
export type { SortableColumnProps };
